import { useMemo } from 'react';

import { TelemetryTypes } from '@store/enums';
import { EnergyFlowScenarioState } from '@store/types';
import {
    useHasGcp,
    useHasBattery,
    useHasInverter,
    useAppSelector,
    useHasWallbox,
    useHasSmartMeter,
    useHasUkSmartMeter,
    useTelemetries,
    useHasHeatPump,
    useHasElectricCar,
    useIsGridXCustomer,
    useHasEmobility,
} from '@store/selectors';

export const useEnergyFlowScenarios = (
    isLive: boolean,
): EnergyFlowScenarioState => {
    const hasGCP = useHasGcp();
    const hasBattery = useHasBattery();
    const hasWallbox = useHasWallbox();
    const hasElectricCar = useHasElectricCar();
    const hasLiveData = useTelemetries(TelemetryTypes.PV_BATTERY).hasLiveData;
    const hasGCPLiveData = useTelemetries(TelemetryTypes.GCP).hasLiveData;
    const hasInverter = useHasInverter();
    const hasSmartMeter = useHasSmartMeter();
    const hasEmobility = useHasEmobility();
    const hasUkSmartMeter = useHasUkSmartMeter();
    const hasReadingStartDate = useAppSelector(
        (state) => state.energy.hasReadingStartDate,
    );
    const hasHeatPump = useHasHeatPump();
    const isGridXCustomer = useIsGridXCustomer();

    const isPvWithSmartMeter = useMemo(
        () => hasSmartMeter && hasInverter,
        [hasInverter, hasSmartMeter],
    );

    const smartMeterOnly = useMemo(
        () => !isPvWithSmartMeter && !hasInverter && hasGCP,
        [hasGCP, hasInverter, isPvWithSmartMeter],
    );

    const isPvWithSmarthome = useMemo(
        () => hasInverter && !hasGCP && (hasEmobility || hasHeatPump),
        [hasGCP, hasInverter, hasEmobility, hasHeatPump],
    );

    const isSmartMeterOnly = useMemo(
        () => smartMeterOnly && !hasEmobility && !hasHeatPump,
        [hasEmobility, smartMeterOnly, hasHeatPump],
    );

    const isSmartMeterAndSmartHome = useMemo(
        () => smartMeterOnly && (hasEmobility || hasHeatPump),
        [hasEmobility, smartMeterOnly, hasHeatPump],
    );

    const isUkSmartMeterNoData = useMemo(
        () => hasUkSmartMeter && !hasReadingStartDate,
        [hasReadingStartDate, hasUkSmartMeter],
    );

    const isUkSmartMeter = useMemo(
        () =>
            hasUkSmartMeter &&
            hasReadingStartDate &&
            !hasInverter &&
            !hasBattery &&
            !hasEmobility &&
            !hasHeatPump,
        [
            hasUkSmartMeter,
            hasEmobility,
            hasReadingStartDate,
            hasBattery,
            hasInverter,
            hasHeatPump,
        ],
    );

    const hasNoDevices = useMemo(
        () =>
            !hasInverter &&
            !hasGCP &&
            !hasWallbox &&
            !hasElectricCar &&
            !hasUkSmartMeter &&
            !hasUkSmartMeter &&
            !hasHeatPump,
        [
            hasGCP,
            hasInverter,
            hasUkSmartMeter,
            hasWallbox,
            hasHeatPump,
            hasElectricCar,
        ],
    );

    // Disabled in https://jira.eon.com/browse/HS-11800
    // const shouldShowSmartMeterUI = useMemo(
    //     () =>
    //         isGBUser() &&
    //         source === UserModel.SourceEnum.OpenRegistration &&
    //         !hasUkSmartMeter,
    //     [hasUkSmartMeter, source],
    // );

    const shouldShowSmartMeterUI = false;

    const isPVonly = useMemo(
        () =>
            !hasBattery &&
            !hasGCP &&
            !hasUkSmartMeter &&
            !hasEmobility &&
            !hasHeatPump,
        [hasGCP, hasEmobility, hasUkSmartMeter, hasBattery, hasHeatPump],
    );

    const isSmartHomeOnly = useMemo(
        () =>
            !hasInverter &&
            !hasGCP &&
            !hasBattery &&
            !hasUkSmartMeter &&
            hasEmobility,
        [hasGCP, hasEmobility, hasUkSmartMeter, hasBattery, hasInverter],
    );

    const hasNoLiveData = useMemo(
        () =>
            isLive &&
            ((hasInverter && !hasLiveData) ||
                (isGridXCustomer && hasGCP && !hasGCPLiveData)),
        [
            hasInverter,
            hasLiveData,
            isLive,
            hasGCP,
            isGridXCustomer,
            hasGCPLiveData,
        ],
    );

    return {
        isPVonly,
        hasWallbox,
        hasElectricCar,
        hasInverter,
        hasNoDevices,
        hasSmartMeter,
        hasNoLiveData,
        smartMeterOnly,
        isUkSmartMeter,
        hasUkSmartMeter,
        hasEmobility,
        isSmartHomeOnly,
        isSmartMeterOnly,
        isPvWithSmarthome,
        isPvWithSmartMeter,
        isUkSmartMeterNoData,
        shouldShowSmartMeterUI,
        isSmartMeterAndSmartHome,
        hasHeatPump,
    };
};
